import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Badge } from "@timeghost/ui/shadcn/badge";
import { Button } from "@timeghost/ui/shadcn/button";
import { Tabs, TabsList, TabsTrigger } from "@timeghost/ui/shadcn/tabs";
import { MoreHorizontal } from "lucide-react";
import { createContext, type JSX, lazy, Suspense, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ActionsMenu, { DropdownMenuItem } from "@/components/menu/actions-menu";
import ConfirmDialog from "@/components/overlays/confirm-dialog";
import EditSessionChrome, { useEditSessionLeave } from "@/components/settings/edit-session-chrome";
import { EditSessionProvider } from "@/components/settings/edit-session-context";
import ProjectDetailLoader from "@/components/settings/projects/project-detail.loader";
import ProjectDetailsLoader from "@/components/settings/projects/project-details.loader";
import ProjectHoursLoader from "@/components/settings/projects/project-hours.loader";
import ProjectReportLoader from "@/components/settings/projects/project-report.loader";
import ProjectTasksLoader from "@/components/settings/projects/project-tasks.loader";
import BoxedPage from "@/components/shell/boxed-page";
import ScrollList from "@/components/ui/scroll-list";
import { hasCreateTaskPermission } from "@/lib/permissions/create-task-permission";
import { canWriteProject } from "@/lib/permissions/permission";
import { projectExternalId } from "@/lib/projects/project-label";
import {
	isTemplateProject,
	resolveProjectDisplayColor,
	type SettingsProject,
	toSettingsProject,
} from "@/lib/projects/types";
import stores from "@/lib/stores/web-api-stores";
import { projectQueryKey, projectsQueryKey, useProject } from "@/queries/projects/useProjects.query";
import { useCurrentUser } from "@/queries/user/useCurrentUser.query";

export const PROJECT_TAB_KEYS = ["details", "tasks", "hours", "report"] as const;
export type ProjectTabKey = (typeof PROJECT_TAB_KEYS)[number];

export const PROJECT_DETAIL_TAB_TO = {
	details: "/settings/projects/$id",
	tasks: "/settings/projects/$id/tasks",
	hours: "/settings/projects/$id/hours",
	report: "/settings/projects/$id/report",
} as const;

const ProjectEditForm = lazy(() => import("@/components/settings/projects/project-edit-form"));
const ProjectTasksPanel = lazy(() => import("@/components/settings/projects/project-tasks-panel"));
const ProjectHoursPanel = lazy(() => import("@/components/settings/projects/project-hours-panel"));
const ProjectReportPanel = lazy(() => import("@/components/settings/projects/project-report-panel"));

const preloadProjectTab = (key: ProjectTabKey): void => {
	if (key === "details") {
		void import("@/components/settings/projects/project-edit-form");
		return;
	}
	if (key === "tasks") {
		void import("@/components/settings/projects/project-tasks-panel");
		return;
	}
	if (key === "hours") {
		void import("@/components/settings/projects/project-hours-panel");
		return;
	}
	void import("@/components/settings/projects/project-report-panel");
};

export const pathToProjectDetailTab = (pathname: string, projectId: string): ProjectTabKey | null => {
	const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
	const base = `/settings/projects/${projectId}`;
	if (path === base) {
		return "details";
	}
	if (path === `${base}/tasks`) {
		return "tasks";
	}
	if (path === `${base}/hours`) {
		return "hours";
	}
	if (path === `${base}/report`) {
		return "report";
	}
	return null;
};

interface ProjectDetailPageProps {
	readonly projectId: string;
	readonly tab: ProjectTabKey;
}

interface ProjectDetailTabsContextValue {
	readonly canManage: boolean;
	readonly project: SettingsProject;
	readonly projectId: string;
}

const ProjectDetailTabsContext = createContext<ProjectDetailTabsContextValue | null>(null);

export const useProjectDetailTabs = (): ProjectDetailTabsContextValue => {
	const value = useContext(ProjectDetailTabsContext);
	if (!value) {
		throw new Error("useProjectDetailTabs must be used under ProjectDetailPage");
	}
	return value;
};

export default function ProjectDetailPage({ projectId, tab }: ProjectDetailPageProps): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useCurrentUser();
	const projectQuery = useProject(projectId);
	const project = projectQuery.data;
	const canManage = canWriteProject(user, projectId, project);

	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

	const deleteMutation = useMutation({
		mutationFn: async () => {
			await stores.projects.actions.delete(projectId);
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: projectsQueryKey, exact: true });
			queryClient.removeQueries({ queryKey: projectQueryKey(projectId) });
			toast.success(t("settings.projects.deleted"));
			void navigate({ to: "/settings/projects" });
		},
		onError: () => {
			toast.error(t("settings.projects.deleteFailed"));
		},
	});

	const completeMutation = useMutation({
		mutationFn: async (completed: boolean) => {
			if (!project) {
				throw new Error("project.missing");
			}
			const updated = await stores.projects.actions.update({
				...project.raw,
				id: project.id,
				completed,
			});
			return toSettingsProject(updated as Record<string, unknown>);
		},
		onSuccess: async (next) => {
			if (next) {
				queryClient.setQueryData(projectQueryKey(projectId), next);
			}
			await queryClient.invalidateQueries({ queryKey: projectsQueryKey, exact: true });
			toast.success(next?.completed ? t("settings.projects.completedToast") : t("settings.projects.reopenedToast"));
		},
		onError: () => {
			toast.error(t("settings.projects.completeFailed"));
		},
	});

	const visibleTabKeys = useMemo((): ProjectTabKey[] => {
		if (!project) {
			return [...PROJECT_TAB_KEYS];
		}
		if (project.useAsDefault) {
			return ["report", "hours", "tasks"];
		}
		return [...PROJECT_TAB_KEYS];
	}, [project]);

	useEffect(() => {
		if (project?.useAsDefault && tab === "details") {
			void navigate({
				to: PROJECT_DETAIL_TAB_TO.report,
				params: { id: projectId },
				replace: true,
			});
		}
	}, [navigate, project?.useAsDefault, projectId, tab]);

	useEffect(() => {
		if (!visibleTabKeys.includes(tab)) {
			const fallback = visibleTabKeys[0] ?? "details";
			if (fallback !== tab) {
				void navigate({
					to: PROJECT_DETAIL_TAB_TO[fallback],
					params: { id: projectId },
					replace: true,
				});
			}
		}
	}, [navigate, projectId, tab, visibleTabKeys]);

	if (projectQuery.isLoading) {
		return <ProjectDetailLoader tab={tab} />;
	}

	if (projectQuery.isError || !project) {
		return (
			<BoxedPage title={t("nav.projects")}>
				<p className="text-destructive text-sm">{t("settings.projects.loadFailed")}</p>
				<Button
					className="mt-3"
					onClick={() => void navigate({ to: "/settings/projects" })}
					type="button"
					variant="outline">
					{t("settings.projects.back")}
				</Button>
			</BoxedPage>
		);
	}

	const showOverflow = canManage && !project.useAsDefault;

	const blockLeavingProject = ({
		currentPath,
		nextPath,
	}: {
		readonly currentPath: string;
		readonly nextPath: string;
	}): boolean => {
		const prefix = `/settings/projects/${projectId}`;
		const stillOnProject = nextPath === prefix || nextPath.startsWith(`${prefix}/`);
		return currentPath.startsWith("/settings/projects/") && !stillOnProject;
	};

	return (
		<EditSessionProvider>
			<EditSessionChrome shouldBlockNavigation={blockLeavingProject}>
				<ProjectDetailLoaded
					canManage={canManage}
					completeMutationPending={completeMutation.isPending}
					confirmDeleteOpen={confirmDeleteOpen}
					deleteMutation={deleteMutation}
					onComplete={() => void completeMutation.mutateAsync(!project.completed)}
					onConfirmDeleteOpenChange={setConfirmDeleteOpen}
					onDelete={() => void deleteMutation.mutateAsync()}
					project={project}
					projectId={projectId}
					showOverflow={showOverflow}
					tab={tab}
					visibleTabKeys={visibleTabKeys}
				/>
			</EditSessionChrome>
		</EditSessionProvider>
	);
}

interface ProjectDetailLoadedProps {
	readonly canManage: boolean;
	readonly completeMutationPending: boolean;
	readonly confirmDeleteOpen: boolean;
	readonly deleteMutation: {
		readonly isPending: boolean;
	};
	readonly onComplete: () => void;
	readonly onConfirmDeleteOpenChange: (open: boolean) => void;
	readonly onDelete: () => void;
	readonly project: SettingsProject;
	readonly projectId: string;
	readonly showOverflow: boolean;
	readonly tab: ProjectTabKey;
	readonly visibleTabKeys: readonly ProjectTabKey[];
}

function ProjectDetailLoaded({
	project,
	projectId,
	canManage,
	showOverflow,
	tab,
	visibleTabKeys,
	confirmDeleteOpen,
	onConfirmDeleteOpenChange,
	onComplete,
	onDelete,
	completeMutationPending,
	deleteMutation,
}: ProjectDetailLoadedProps): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const leave = useEditSessionLeave();
	const isTemplate = isTemplateProject(project);
	const headerColor = resolveProjectDisplayColor(project);

	const headerActions = (
		<div className="flex items-center gap-2">
			<Button
				onClick={() => void navigate({ to: isTemplate ? "/settings/projects/templates" : "/settings/projects" })}
				size="sm"
				type="button"
				variant="outline">
				{isTemplate ? t("settings.projects.templates.back") : t("settings.projects.back")}
			</Button>
			{showOverflow ? (
				<>
					<ActionsMenu
						icon={<MoreHorizontal className="size-4" />}
						trigger={
							<Button aria-label={t("settings.projects.rowActions")} size="icon-sm" type="button" variant="outline" />
						}>
						<DropdownMenuItem disabled={completeMutationPending} onClick={onComplete}>
							{project.completed ? t("settings.projects.undoComplete") : t("settings.projects.complete")}
						</DropdownMenuItem>
						<DropdownMenuItem
							disabled={project.completed}
							onClick={() => onConfirmDeleteOpenChange(true)}
							variant="destructive">
							{t("settings.projects.delete")}
						</DropdownMenuItem>
					</ActionsMenu>
					<ConfirmDialog
						cancelLabel={t("settings.projects.cancel")}
						confirmLabel={t("settings.projects.delete")}
						confirmText={project.name}
						description={t("settings.projects.deleteDescription", { name: project.name })}
						onConfirm={onDelete}
						onOpenChange={onConfirmDeleteOpenChange}
						open={confirmDeleteOpen}
						pending={deleteMutation.isPending}
						title={t("settings.projects.deleteTitle")}
					/>
				</>
			) : null}
		</div>
	);

	return (
		<ProjectDetailTabsContext.Provider value={{ canManage, project, projectId }}>
			<Tabs
				className="flex min-h-0 flex-1 flex-col gap-0"
				onValueChange={(next) => {
					if (next === tab) {
						return;
					}
					const nextTab = visibleTabKeys.find((key) => key === next);
					if (!nextTab) {
						return;
					}
					const go = (): void => {
						void navigate({
							params: { id: projectId },
							to: PROJECT_DETAIL_TAB_TO[nextTab],
						});
					};
					if (leave.dirty) {
						void leave.confirmLeave().then((ok) => {
							if (ok) {
								go();
							}
						});
						return;
					}
					go();
				}}
				value={tab}>
				<BoxedPage
					bodyMode="fill"
					headerContent={
						<div className="flex w-full min-w-0 flex-col">
							<div className="flex min-h-12 flex-wrap items-center justify-between gap-3 px-4 py-3">
								<div className="min-w-0">
									<div className="flex min-w-0 items-center gap-2">
										{headerColor ? (
											<span
												aria-hidden
												className="size-2.5 shrink-0 rounded-full"
												style={{ backgroundColor: headerColor }}
											/>
										) : null}
										<h1 className="truncate font-medium text-[15px] tracking-tight">{project.name}</h1>
										{isTemplate ? (
											<Badge className="font-normal" variant="secondary">
												{t("settings.projects.templates.badge")}
											</Badge>
										) : null}
									</div>
									{projectExternalId(project) ? (
										<p className="mt-0.5 truncate text-[13px] text-muted-foreground">{projectExternalId(project)}</p>
									) : null}
									{isTemplate ? (
										<p className="mt-0.5 truncate text-[13px] text-muted-foreground">
											{t("settings.projects.templates.pageHint")}
										</p>
									) : null}
								</div>
								{headerActions}
							</div>
							<TabsList className="px-4" variant="line">
								{visibleTabKeys.map((key) => (
									<TabsTrigger
										key={key}
										onFocus={() => preloadProjectTab(key)}
										onMouseEnter={() => preloadProjectTab(key)}
										value={key}>
										{t(`settings.projects.tabs.${key}`)}
									</TabsTrigger>
								))}
							</TabsList>
						</div>
					}>
					<div className="flex h-full min-h-0 flex-col">
						{tab === "details" ? (
							<Suspense fallback={<ProjectDetailsLoader />}>
								<ProjectDetailsTab />
							</Suspense>
						) : null}
						{tab === "tasks" ? (
							<Suspense fallback={<ProjectTasksLoader showToolbar />}>
								<ProjectTasksTab />
							</Suspense>
						) : null}
						{tab === "hours" ? (
							<Suspense fallback={<ProjectHoursLoader showToolbar />}>
								<ProjectHoursTab />
							</Suspense>
						) : null}
						{tab === "report" ? (
							<Suspense fallback={<ProjectReportLoader showToolbar />}>
								<ProjectReportTab />
							</Suspense>
						) : null}
					</div>
				</BoxedPage>
			</Tabs>
		</ProjectDetailTabsContext.Provider>
	);
}

function ProjectDetailsTab(): JSX.Element {
	const { canManage, project } = useProjectDetailTabs();
	const leave = useEditSessionLeave();
	const navigate = useNavigate();

	return (
		<ScrollList className="h-0 flex-1" viewportClassName="overflow-anchor-none pb-60">
			<div className="py-2">
				<ProjectEditForm
					canManage={canManage}
					onManageTasks={() => {
						void leave.confirmLeave().then((ok) => {
							if (!ok) {
								return;
							}
							void navigate({
								to: PROJECT_DETAIL_TAB_TO.tasks,
								params: { id: project.id },
							});
						});
					}}
					project={project}
				/>
			</div>
		</ScrollList>
	);
}

function ProjectTasksTab(): JSX.Element {
	const { project } = useProjectDetailTabs();
	const { user } = useCurrentUser();
	const canManage = hasCreateTaskPermission(user, project);
	return (
		<div className="flex h-full min-h-0 flex-col">
			<ProjectTasksPanel canManage={canManage && !project.completed} project={project} />
		</div>
	);
}

function ProjectHoursTab(): JSX.Element {
	const { projectId } = useProjectDetailTabs();

	return (
		<div className="flex h-full min-h-0 flex-col">
			<ProjectHoursPanel projectId={projectId} />
		</div>
	);
}

function ProjectReportTab(): JSX.Element {
	const { project } = useProjectDetailTabs();

	return (
		<ScrollList className="h-0 flex-1" viewportClassName="overflow-anchor-none pb-60">
			<div className="py-2">
				<ProjectReportPanel project={project} />
			</div>
		</ScrollList>
	);
}
